package top.wcpe.allincore.ui.web.docs

import kotlinx.browser.window
import kotlin.js.Promise
import kotlin.js.json

/**
 * 加载并校验 docs-manifest.json。
 * 失败时返回中文诊断，不抛到未处理 Promise。
 */
internal class DocsManifestLoader(
    private val baseUri: String = docsBaseUri(),
    private val fetchText: (String) -> Promise<String> = ::defaultFetchText,
) {
    fun load(): Promise<DocsManifestLoadResult> {
        val url = resolveUrl("docs-manifest.json")
        return fetchText(url).then { text ->
            try {
                DocsManifestLoadResult.Ok(parseAndValidate(JSON.parse(text)))
            } catch (error: Throwable) {
                DocsManifestLoadResult.Error(error.message ?: "docs-manifest 解析失败")
            }
        }.catch { reason ->
            DocsManifestLoadResult.Error("docs-manifest 加载失败：${reason.toString()}")
        }
    }

    fun loadText(relativePath: String): Promise<String> = fetchText(resolveUrl(relativePath))

    private fun resolveUrl(relativePath: String): String {
        val base = if (baseUri.endsWith("/")) baseUri else "$baseUri/"
        return js("new globalThis.URL(relativePath, base).href") as String
    }
}

internal sealed class DocsManifestLoadResult {
    data class Ok(val manifest: DocsManifest) : DocsManifestLoadResult()
    data class Error(val message: String) : DocsManifestLoadResult()
}

internal fun parseAndValidate(raw: dynamic): DocsManifest {
    val schemaVersion = (raw.schemaVersion as? Number)?.toInt()
        ?: throw IllegalArgumentException("docs-manifest.schemaVersion 缺失或非法")
    if (schemaVersion != 1) throw IllegalArgumentException("docs-manifest.schemaVersion 仅支持 1")
    val list = raw.components ?: throw IllegalArgumentException("docs-manifest.components 缺失")
    val length = (list.length as? Number)?.toInt() ?: throw IllegalArgumentException("docs-manifest.components 不是数组")
    if (length == 0) throw IllegalArgumentException("docs-manifest.components 不能为空")
    val components = ArrayList<DocsComponentEntry>(length)
    val seen = HashSet<String>()
    for (index in 0 until length) {
        val item = list[index] ?: throw IllegalArgumentException("docs-manifest.components[$index] 为空")
        val entry = parseComponent(item, index)
        if (!seen.add(entry.id)) throw IllegalArgumentException("docs-manifest 组件 id 重复：${entry.id}")
        validateLogicalPath(entry.sourcePath, "sourcePath")
        entry.examples.forEach { example -> validateLogicalPath(example.entry, "example.entry") }
        entry.dependencies.components.forEach { validateLogicalPath(it, "dependencies.components") }
        entry.dependencies.mocks.forEach { validateLogicalPath(it, "dependencies.mocks") }
        entry.dependencies.models.forEach { validateLogicalPath(it, "dependencies.models") }
        entry.dependencies.textures.forEach { validateLogicalPath(it, "dependencies.textures") }
        components += entry
    }
    return DocsManifest(schemaVersion, components.sortedWith(compareBy({ it.order }, { it.id })))
}

private fun parseComponent(raw: dynamic, index: Int): DocsComponentEntry {
    val id = requireString(raw.id, "components[$index].id")
    if (!Regex("^[a-z0-9][a-z0-9\\-]*$").matches(id)) {
        throw IllegalArgumentException("components[$index].id 非法：$id")
    }
    val propsRaw = raw.props ?: json()
    val propsLength = (propsRaw.length as? Number)?.toInt() ?: 0
    val props = (0 until propsLength).map { propIndex ->
        val prop = propsRaw[propIndex]
        DocsPropEntry(
            name = requireString(prop.name, "components[$index].props[$propIndex].name"),
            required = prop.required as? Boolean ?: false,
            type = requireString(prop.type, "components[$index].props[$propIndex].type"),
            defaultValue = prop.default as? String,
            description = requireString(prop.description, "components[$index].props[$propIndex].description"),
        )
    }
    if (props.any { it.required && it.defaultValue != null }) {
        // 允许 required 无 default；有 default 也可，仅要求必填 props 在示例中有值（由构建 lint 校验）
    }
    val examplesRaw = raw.examples ?: throw IllegalArgumentException("components[$index].examples 缺失")
    val examplesLength = (examplesRaw.length as? Number)?.toInt()
        ?: throw IllegalArgumentException("components[$index].examples 不是数组")
    if (examplesLength == 0) throw IllegalArgumentException("components[$index].examples 不能为空")
    val examples = (0 until examplesLength).map { exampleIndex ->
        val example = examplesRaw[exampleIndex]
        DocsExampleEntry(
            id = requireString(example.id, "components[$index].examples[$exampleIndex].id"),
            title = requireString(example.title, "components[$index].examples[$exampleIndex].title"),
            entry = requireString(example.entry, "components[$index].examples[$exampleIndex].entry"),
        )
    }
    val capabilitiesRaw = raw.capabilities ?: throw IllegalArgumentException("components[$index].capabilities 缺失")
    val dependenciesRaw = raw.dependencies ?: json()
    return DocsComponentEntry(
        id = id,
        displayName = requireString(raw.displayName, "components[$index].displayName"),
        category = requireString(raw.category, "components[$index].category"),
        order = (raw.order as? Number)?.toInt() ?: index * 10,
        sourcePath = requireString(raw.sourcePath, "components[$index].sourcePath"),
        examples = examples,
        props = props,
        themes = readStringArray(raw.themes),
        capabilities = DocsCapabilities(
            web = requireCapability(capabilitiesRaw.web, "components[$index].capabilities.web"),
            jcef = requireCapability(capabilitiesRaw.jcef, "components[$index].capabilities.jcef"),
            fabric = requireCapability(capabilitiesRaw.fabric, "components[$index].capabilities.fabric"),
        ),
        limits = readStringArray(raw.limits),
        dependencies = DocsDependencies(
            components = readStringArray(dependenciesRaw.components),
            themes = readStringArray(dependenciesRaw.themes),
            mocks = readStringArray(dependenciesRaw.mocks),
            models = readStringArray(dependenciesRaw.models),
            textures = readStringArray(dependenciesRaw.textures),
        ),
    )
}

private fun requireString(value: dynamic, field: String): String {
    val text = value as? String
    if (text.isNullOrBlank()) throw IllegalArgumentException("$field 必须是非空字符串")
    return text
}

private fun requireCapability(value: dynamic, field: String): String {
    val text = requireString(value, field)
    if (text !in setOf("full", "limited", "unsupported")) {
        throw IllegalArgumentException("$field 只能是 full/limited/unsupported")
    }
    return text
}

private fun readStringArray(value: dynamic): List<String> {
    if (value == null) return emptyList()
    val length = (value.length as? Number)?.toInt() ?: return emptyList()
    return (0 until length).mapNotNull { index -> value[index] as? String }.filter { it.isNotBlank() }
}

private fun validateLogicalPath(path: String, field: String) {
    if (path.isBlank() || path.startsWith('/') || path.endsWith('/')) {
        throw IllegalArgumentException("$field 必须是逻辑相对路径：$path")
    }
    if ('\\' in path || ':' in path || path.contains("..")) {
        throw IllegalArgumentException("$field 禁止绝对路径/父目录穿越：$path")
    }
}

private fun docsBaseUri(): String = js("new globalThis.URL('.', document.baseURI).href") as String

private fun defaultFetchText(url: String): Promise<String> =
    Promise { resolve, reject ->
        val fetchPromise = js("globalThis.fetch(url)").unsafeCast<Promise<dynamic>>()
        fetchPromise.then<Unit>(
            onFulfilled = { response ->
                readFetchText(response, resolve, reject)
            },
            onRejected = { reason ->
                reject(IllegalStateException(reason.toString()))
            },
        )
    }

private fun readFetchText(
    response: dynamic,
    resolve: (String) -> Unit,
    reject: (Throwable) -> Unit,
) {
    val ok = response.ok as? Boolean ?: false
    if (!ok) {
        val status = (response.status as? Number)?.toInt() ?: -1
        reject(IllegalStateException("HTTP $status"))
        return
    }
    response.text().unsafeCast<Promise<String>>().then<Unit>(
        onFulfilled = { text -> resolve(text) },
        onRejected = { reason -> reject(IllegalStateException(reason.toString())) },
    )
}
